import React from 'react';
import { motion } from 'framer-motion';
import { Layout } from '../components/Layout';
import { ShoppingBagIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
const productCategories = [{
  title: 'Classic Shaker',
  description: 'Timeless design with clean lines, perfect for both traditional and modern kitchens.',
  image: 'https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?auto=format&fit=crop&q=80&w=800',
  features: ['Solid Wood Core', 'Durable Finish', 'Multiple Colors']
}, {
  title: 'Modern Slab',
  description: 'Sleek, minimalist profile for a contemporary high-end look.',
  image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=800',
  features: ['Seamless Edge', 'Easy Clean', 'High Gloss Options']
}, {
  title: 'Raised Panel',
  description: 'Elegant and sophisticated detailing for a classic luxury aesthetic.',
  image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&q=80&w=800',
  features: ['Intricate Detail', 'Premium Texture', 'Rich Wood Tones']
}];
export function ProductsPage() {
  return <Layout data-id="element-518">
      <section className="py-24 px-6 bg-alabaster border-b-3 border-charcoal" data-id="element-519">
        <div className="max-w-7xl mx-auto text-center" data-id="element-520">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="inline-block bg-espresso/10 text-espresso px-4 py-2 font-bold uppercase tracking-widest text-xs mb-6" data-id="element-521">
            Our Collection
          </motion.span>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-5xl md:text-7xl font-bold mb-8" data-id="element-522">
            Premium{' '}
            <span className="italic font-serif font-normal text-espresso" data-id="element-523">
              Door Styles
            </span>
            .
          </motion.h1>
          <p className="text-xl text-charcoal/60 max-w-2xl mx-auto leading-relaxed" data-id="element-524">
            Explore our curated selection of high-quality cabinet doors and
            finishes, engineered for durability and style.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-id="element-525">
        <div className="max-w-7xl mx-auto" data-id="element-526">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-527">
            {productCategories.map((product, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="group" data-id="element-528">
                <div className="aspect-[4/5] overflow-hidden border-3 border-charcoal mb-8 relative" data-id="element-529">
                  <img src={product.image} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-530" />
                  <div className="absolute top-4 right-4 bg-white border-2 border-charcoal p-2" data-id="element-531">
                    <ShieldCheckIcon className="w-5 h-5 text-espresso" data-id="element-532" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-533">{product.title}</h3>
                <p className="text-charcoal/60 mb-6 leading-relaxed" data-id="element-534">
                  {product.description}
                </p>
                <ul className="space-y-2 mb-8" data-id="element-535">
                  {product.features.map((feature, i) => <li key={i} className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-charcoal/40" data-id="element-536">
                      <SparklesIcon className="w-3 h-3 text-espresso" data-id="element-537" />
                      {feature}
                    </li>)}
                </ul>
                <a href="/book" className="inline-block bg-charcoal text-alabaster px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-espresso transition-colors w-full text-center" data-id="element-538">
                  Request Sample
                </a>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Hardware Section */}
      <section className="py-24 px-6 bg-charcoal text-alabaster" data-id="element-539">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-540">
          <div className="space-y-8" data-id="element-541">
            <span className="text-espresso font-bold uppercase tracking-widest text-xs" data-id="element-542">
              Finishing Touches
            </span>
            <h2 className="text-4xl md:text-5xl font-bold" data-id="element-543">
              Premium Hardware & Accessories
            </h2>
            <p className="text-alabaster/60 leading-relaxed text-lg" data-id="element-544">
              Complete your transformation with our selection of designer
              handles, soft-close hinges, and organizational inserts. Every
              detail is chosen for its quality and tactile feel.
            </p>
            <div className="grid grid-cols-2 gap-8 pt-4" data-id="element-545">
              <div data-id="element-546">
                <h4 className="font-bold mb-2 text-espresso" data-id="element-547">Soft-Close</h4>
                <p className="text-sm text-alabaster/40 text-balance" data-id="element-548">
                  Standard on all new door installations for a silent, luxury
                  feel.
                </p>
              </div>
              <div data-id="element-549">
                <h4 className="font-bold mb-2 text-espresso" data-id="element-550">Designer Pulls</h4>
                <p className="text-sm text-alabaster/40 text-balance" data-id="element-551">
                  From brushed brass to matte black, find the perfect accent.
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-552">
            <img src="https://images.unsplash.com/photo-1556912167-45710a7156aa?auto=format&fit=crop&q=80&w=600" alt="Hardware 1" className="border-2 border-white/10 aspect-square object-cover" data-id="element-553" />
            <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=600" alt="Hardware 2" className="border-2 border-white/10 aspect-square object-cover mt-8" data-id="element-554" />
          </div>
        </div>
      </section>
    </Layout>;
}